import { useMemo } from 'react';
import { useWriterProjectsOverview, type WriterProjectOverview } from '@/hooks/useWriterProjectsOverview';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useSharedWithMe } from '@/hooks/papers/useSharedWithMe';

export type QueueKind =
  | 'compile_failed'
  | 'stale_draft'
  | 'review_request'
  | 'review_draft'
  | 'invite';

export interface QueueItem {
  id: string;
  kind: QueueKind;
  title: string;
  detail: string;
  actionLabel: string;
  href: string;
}

/** Writing chores come before review chores; within each, urgency first. */
const ORDER: Record<QueueKind, number> = {
  compile_failed: 0,
  stale_draft: 1,
  review_request: 2,
  review_draft: 3,
  invite: 4,
};

const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const daysSince = (iso: string): number =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/** Writer-side signals derived from the draft list. Exported for tests. */
export function writerQueueItems(projects: WriterProjectOverview[]): QueueItem[] {
  const items: QueueItem[] = [];
  for (const p of projects) {
    if (p.compileStatus === 'failed') {
      items.push({
        id: `compile-${p.id}`,
        kind: 'compile_failed',
        title: p.title || 'Untitled draft',
        detail: 'The last compile failed — the agent can repair it.',
        actionLabel: 'Open draft',
        href: `/write/${p.id}`,
      });
      continue;
    }
    if (Date.now() - new Date(p.updatedAt).getTime() > STALE_AFTER_MS) {
      items.push({
        id: `stale-${p.id}`,
        kind: 'stale_draft',
        title: p.title || 'Untitled draft',
        detail: `Untouched for ${daysSince(p.updatedAt)} days.`,
        actionLabel: 'Pick it up',
        href: `/write/${p.id}`,
      });
    }
  }
  return items;
}

export function sortQueue(items: QueueItem[]): QueueItem[] {
  return [...items].sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
}

/**
 * One list answering "what needs me". Pure composition of existing queries —
 * writing signals first, review chores after.
 */
export function useNeedsYouQueue() {
  const { projects, loading } = useWriterProjectsOverview();
  const { draftReviews, pendingRequests } = useDashboardStats();
  const { data: shared } = useSharedWithMe();

  const items = useMemo(() => {
    const list = writerQueueItems(projects);

    if (pendingRequests > 0) {
      list.push({
        id: 'review-requests',
        kind: 'review_request',
        title: pendingRequests === 1 ? 'A review was requested from you' : `${pendingRequests} reviews requested from you`,
        detail: 'Someone asked you to referee their manuscript.',
        actionLabel: 'Open review desk',
        href: '/dashboard-v2#review-desk',
      });
    }

    if (draftReviews > 0) {
      list.push({
        id: 'review-drafts',
        kind: 'review_draft',
        title: draftReviews === 1 ? 'An unfinished review' : `${draftReviews} unfinished reviews`,
        detail: 'Started but never published.',
        actionLabel: 'Finish it',
        href: '/dashboard-v2#review-desk',
      });
    }

    const inviteCount = shared?.rows?.length ?? 0;
    if (inviteCount > 0) {
      list.push({
        id: 'invites',
        kind: 'invite',
        title: inviteCount === 1 ? 'A paper was shared with you' : `${inviteCount} papers shared with you`,
        detail: 'Invitations from other researchers.',
        actionLabel: 'See them',
        href: '/dashboard-v2#review-desk',
      });
    }

    return sortQueue(list);
  }, [projects, pendingRequests, draftReviews, shared]);

  return { items, loading };
}
